import { useMemo } from 'react';
import type { Ticket } from '@/lib/mock';
import { LayoutGrid, Inbox, AlertCircle, FileText, CheckCircle, Zap, Clock, Activity } from 'lucide-react';
import { ThemeToggle } from '@/components/theme-toggle';
import { useAuth } from '@/lib/auth';

export function UserDashboard({ tickets, onNewTicket, onSelectTicket, onViewAll }: { tickets: Ticket[], onNewTicket?: () => void, onSelectTicket?: (id: string) => void, onViewAll?: () => void }) {
  const { user, session } = useAuth();
  const currentUserId = user?.id || session?.user?.id;
  const currentUserName = (user?.displayName || user?.name || session?.user?.name || '').toLowerCase().trim();
  const currentUserEmail = (user?.email || session?.user?.email || '').toLowerCase().trim();
  
  const myTickets = useMemo(() => {
    return tickets.filter(t => {
      if (currentUserId && (t.requesterId === currentUserId || (t as any).createdBy === currentUserId)) return true;
      if (currentUserEmail && (
        (t.requesterEmail && t.requesterEmail.toLowerCase().trim() === currentUserEmail) ||
        (t.requester && t.requester.toLowerCase().trim() === currentUserEmail)
      )) return true;
      if (currentUserName && t.requester && t.requester.toLowerCase().trim() === currentUserName) return true;
      if (!t.requesterId && !t.requesterEmail && (!t.requester || t.requester === '—')) return true;
      return false;
    });
  }, [tickets, currentUserId, currentUserEmail, currentUserName]);

  // Sort by createdAt descending so recent tickets are always at the top!
  const sortedTickets = useMemo(() => {
    return [...myTickets].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [myTickets]);

  const openTickets = useMemo(() => sortedTickets.filter(t => t.state !== 'Closed' && t.state !== 'Resolved'), [sortedTickets]);
  const closedTickets = useMemo(() => sortedTickets.filter(t => t.state === 'Closed' || t.state === 'Resolved'), [sortedTickets]);
  const pendingTickets = useMemo(() => sortedTickets.filter(t => t.state === 'Pending' || t.state === 'On Hold'), [sortedTickets]);
  const recentlyUpdated = useMemo(() => sortedTickets.filter(t => (Date.now() - (t.createdAt || 0)) < 7 * 24 * 60 * 60 * 1000), [sortedTickets]);

  return (
    <div className="flex h-full flex-col bg-theme p-6 overflow-y-auto">
      <div className="mx-auto max-w-6xl w-full">
        
        {/* Top Header with Theme Toggle */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="font-display text-2xl font-semibold tracking-tight">Dashboard Home</h1>
          <ThemeToggle />
        </div>



        {/* Quick Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="neo rounded-[2rem] p-5 hover:-translate-y-0.5 transition-all duration-300">
            <div className="flex items-center gap-3 text-blue-500 mb-2">
              <Inbox className="h-5 w-5 drop-shadow-sm" />
              <h3 className="font-medium text-sm">Open Tickets</h3>
            </div>
            <p className="text-3xl font-bold text-foreground tracking-tight">{openTickets.length}</p>
          </div>
          
          <div className="neo rounded-[2rem] p-5 hover:-translate-y-0.5 transition-all duration-300">
            <div className="flex items-center gap-3 text-orange-500 mb-2">
              <Clock className="h-5 w-5 drop-shadow-sm" />
              <h3 className="font-medium text-sm">Pending</h3>
            </div>
            <p className="text-3xl font-bold text-foreground tracking-tight">{pendingTickets.length}</p>
          </div>

          <div className="neo rounded-[2rem] p-5 hover:-translate-y-0.5 transition-all duration-300">
            <div className="flex items-center gap-3 text-emerald-500 mb-2">
              <CheckCircle className="h-5 w-5 drop-shadow-sm" />
              <h3 className="font-medium text-sm">Closed Tickets</h3>
            </div>
            <p className="text-3xl font-bold text-foreground tracking-tight">{closedTickets.length}</p>
          </div>

          <div className="neo rounded-[2rem] p-5 hover:-translate-y-0.5 transition-all duration-300">
            <div className="flex items-center gap-3 text-purple-500 mb-2">
              <Activity className="h-5 w-5 drop-shadow-sm" />
              <h3 className="font-medium text-sm">Recent Activity</h3>
            </div>
            <p className="text-3xl font-bold text-foreground tracking-tight">{recentlyUpdated.length}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Quick Actions */}
            <div className="neo rounded-[2rem] p-6 transition-all duration-300">
              <h2 className="font-display text-lg font-medium mb-5 flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-500 fill-amber-500/20" /> Quick Actions
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <button onClick={onNewTicket} className="group flex flex-col items-center justify-center p-4 rounded-xl bg-gradient-to-b from-muted/50 to-muted/10 border border-border/30 hover:border-primary/30 hover:shadow-md hover:bg-primary/5 hover:-translate-y-1 transition-all duration-300 gap-3 text-sm font-medium text-center">
                  <span className="w-12 h-12 rounded-full bg-background border border-border/50 flex items-center justify-center text-primary group-hover:scale-110 group-hover:bg-primary/10 transition-transform duration-300 shadow-sm">
                    <Inbox className="h-5 w-5" />
                  </span>
                  New Ticket
                </button>
                <button onClick={onNewTicket} className="group flex flex-col items-center justify-center p-4 rounded-xl bg-gradient-to-b from-muted/50 to-muted/10 border border-border/30 hover:border-primary/30 hover:shadow-md hover:bg-primary/5 hover:-translate-y-1 transition-all duration-300 gap-3 text-sm font-medium text-center">
                  <span className="w-12 h-12 rounded-full bg-background border border-border/50 flex items-center justify-center text-primary group-hover:scale-110 group-hover:bg-primary/10 transition-transform duration-300 shadow-sm">
                    <LayoutGrid className="h-5 w-5" />
                  </span>
                  Request Hardware
                </button>
                <button onClick={onNewTicket} className="group flex flex-col items-center justify-center p-4 rounded-xl bg-gradient-to-b from-muted/50 to-muted/10 border border-border/30 hover:border-primary/30 hover:shadow-md hover:bg-primary/5 hover:-translate-y-1 transition-all duration-300 gap-3 text-sm font-medium text-center">
                  <span className="w-12 h-12 rounded-full bg-background border border-border/50 flex items-center justify-center text-primary group-hover:scale-110 group-hover:bg-primary/10 transition-transform duration-300 shadow-sm">
                    <FileText className="h-5 w-5" />
                  </span>
                  Software Access
                </button>
                <button onClick={onNewTicket} className="group flex flex-col items-center justify-center p-4 rounded-xl bg-gradient-to-b from-muted/50 to-muted/10 border border-border/30 hover:border-primary/30 hover:shadow-md hover:bg-primary/5 hover:-translate-y-1 transition-all duration-300 gap-3 text-sm font-medium text-center">
                  <span className="w-12 h-12 rounded-full bg-background border border-border/50 flex items-center justify-center text-primary group-hover:scale-110 group-hover:bg-primary/10 transition-transform duration-300 shadow-sm">
                    <AlertCircle className="h-5 w-5" />
                  </span>
                  Report Issue
                </button>
              </div>
            </div>

            {/* Ticket Status Summary */}
            <div className="neo rounded-[2rem] p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-display text-lg font-medium">Ticket Status Summary</h2>
                <button onClick={onViewAll} className="text-xs text-primary font-medium hover:underline">View All</button>
              </div>
              {openTickets.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground bg-muted/30 rounded-lg">
                  You have no open tickets. Great!
                </div>
              ) : (
                <div className="space-y-3">
                  {openTickets.slice(0, 4).map((t, i) => (
                    <div 
                      key={i} 
                      onClick={() => onSelectTicket?.(t.id)}
                      className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 border border-border/60 bg-background rounded-lg hover:border-primary/40 transition gap-2 cursor-pointer"
                    >
                      <div>
                        <div className="font-medium text-sm mb-1">{t.subject}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                          <span className="bg-muted px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold">{t.ref}</span>
                          <span>Opened: {new Date(t.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-1 bg-primary/10 text-primary text-[11px] rounded-full font-medium">
                          {t.state}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Latest Announcements */}
            <div className="neo rounded-[2rem] p-6">
              <h2 className="font-display text-lg font-medium mb-3 flex items-center gap-2">
                <FileText className="h-5 w-5 text-muted-foreground" /> Announcements
              </h2>
              <div className="space-y-3">
                <div className="p-3 bg-muted/40 rounded-lg border border-border/50">
                  <h3 className="font-medium text-sm mb-1 text-foreground">Upcoming IT Maintenance</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">The main file server will be down for maintenance this Saturday from 2 AM to 4 AM UTC. Please save your work accordingly.</p>
                </div>
                <div className="p-3 bg-muted/40 rounded-lg border border-border/50">
                  <h3 className="font-medium text-sm mb-1 text-foreground">New Security Policy</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">Please ensure you change your passwords every 90 days. See knowledge base for more details.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
